import { apiClient } from './api';
import type {
  GlobalSearchCounts,
  GlobalSearchResponse,
  SearchCategory,
  SearchFilterTab,
  SearchResultItem,
  SearchSortMode,
} from '../types/search';

export const SEARCH_CATEGORIES: SearchCategory[] = [
  'contacts',
  'opportunities',
  'jobs',
  'proposals',
  'invoices',
  'tasks',
  'messages',
];

export const EMPTY_SEARCH_COUNTS: GlobalSearchCounts = {
  all: 0,
  contacts: 0,
  opportunities: 0,
  jobs: 0,
  proposals: 0,
  invoices: 0,
  tasks: 0,
  messages: 0,
};

const emptyResults = (): Record<SearchCategory, SearchResultItem[]> => ({
  contacts: [],
  opportunities: [],
  jobs: [],
  proposals: [],
  invoices: [],
  tasks: [],
  messages: [],
});

/** Must match the backend's MAX_QUERY_LENGTH, or longer queries 400. */
export const MAX_SEARCH_QUERY_LENGTH = 100;

export interface GlobalSearchParams {
  q: string;
  category?: SearchFilterTab;
  sort?: SearchSortMode;
}

export async function fetchGlobalSearch({
  q,
  category = 'all',
  sort = 'relevance',
}: GlobalSearchParams): Promise<GlobalSearchResponse> {
  const trimmed = q.trim().slice(0, MAX_SEARCH_QUERY_LENGTH);
  if (!trimmed) {
    return { query: '', counts: { ...EMPTY_SEARCH_COUNTS }, best_match: null, results: emptyResults() };
  }

  const params = new URLSearchParams({ q: trimmed, category, sort });
  const { data } = await apiClient.get<Partial<GlobalSearchResponse> | null>(
    `/api/core/search/?${params.toString()}`,
  );
  if (!data || typeof data !== 'object' || !data.counts) {
    throw new Error('Search returned an unexpected response. Please try again.');
  }
  // Spread over the empty shapes so an older backend missing a category can't crash the dialog.
  return {
    query: data.query ?? trimmed,
    counts: { ...EMPTY_SEARCH_COUNTS, ...data.counts },
    best_match: data.best_match ?? null,
    results: { ...emptyResults(), ...(data.results ?? {}) },
  };
}

/** Where a search hit opens in the web app. */
export function searchResultHref(item: Pick<SearchResultItem, 'id' | 'category' | 'contact_id'>): string {
  switch (item.category) {
    case 'contacts':
      return `/crm/contacts/${item.id}`;
    case 'opportunities':
      return `/crm/opportunities/${item.id}`;
    case 'jobs':
      return `/operations/jobs/${item.id}`;
    case 'proposals':
      return `/crm/proposals/${item.id}`;
    case 'invoices':
      return `/operations/billing/invoices/${item.id}`;
    case 'tasks':
      // Same deep link the task notifications use; TasksPage opens the drawer.
      return `/operations/tasks?task=${item.id}`;
    case 'messages':
      // The web Inbox has no per-item route, so open the conversation's contact.
      return item.contact_id ? `/crm/contacts/${item.contact_id}` : '/inbox';
  }
}

// ---- Recent searches ---------------------------------------------------------

const MAX_RECENT_SEARCHES = 8;

/**
 * Recent searches belong to a person in an organization, never the whole
 * browser — the same scoping as board preferences — so history never leaks
 * across tenants or users on a shared machine.
 */
export function recentSearchesKey(
  user: { id: number; organization?: { id: string } | null } | null,
): string | null {
  const organization = user?.organization?.id;
  return user?.id && organization ? `apexos:global-search:recent:${organization}:${user.id}` : null;
}

export function readRecentSearches(key: string | null): string[] {
  if (!key) return [];
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((q): q is string => typeof q === 'string' && q.trim().length > 0)
      : [];
  } catch {
    return [];
  }
}

export function addRecentSearch(key: string | null, query: string): string[] {
  const trimmed = query.trim();
  const current = readRecentSearches(key);
  if (!key || !trimmed) return current;
  const lower = trimmed.toLowerCase();
  const next = [trimmed, ...current.filter((q) => q.toLowerCase() !== lower)].slice(
    0,
    MAX_RECENT_SEARCHES,
  );
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* Search must still work without storage (private browsing, quota). */
  }
  return next;
}

export function clearRecentSearches(key: string | null): void {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* Ignore storage errors. */
  }
}
